// Import ES6 modules directly (they're not Angular services)
const QueryClient = require("@viz-ui/services/query/queryClient/queryClient").default;
const QueryGenerator = require("@viz-ui/services/query/queryGenerator/queryGenerator").default;
const QueryErrorInterpreter = require("@viz-ui/services/query/queryErrorInterpreter/queryErrorInterpreter.service")
  .default;
const TableDataAdapter = require("@viz-ui/services/query/queryAdapters/tableDataAdapter").default;
const ApiPath = require("@viz-ui/services/apiPath/apiPathService").default;
const backendApi = require("@results/services/apiCall/backendApi").default;

// Import local services
const { DataTableBackendService } = require("./dataTableBackendService");
const { DataTableQueryBackendService } = require("./dataTableQueryBackendService");

/**
 * Simple Feature Flipper Bridge
 *
 * This approach decorates the existing services after they're loaded
 * to avoid module loading conflicts.
 */

// Apply flippers using Angular's config phase
angular.module("acl.visualizer.dataTable.backend").config(function($provide) {
  // Setting up feature flipper decorators...

  // Decorate DataTableBackendService
  $provide.decorator("DataTableBackendService", function($delegate, $injector) {
    try {
      const AppConfig = $injector.get("AppConfig");

      if (AppConfig.features && AppConfig.features.migratedDataTableBackendService) {
        // Using TypeScript DataTableBackendService
        return createTypeScriptDataTableBackendService($injector, AppConfig);
      }
      // Using Angular DataTableBackendService
      return $delegate;
    } catch (error) {
      // Error in DataTableBackendService decorator - fallback to original
      return $delegate;
    }
  });

  // Decorate DataTableQueryBackendService
  $provide.decorator("DataTableQueryBackendService", function($delegate, $injector) {
    try {
      const AppConfig = $injector.get("AppConfig");

      if (AppConfig.features && AppConfig.features.migratedDataTableQueryBackendService) {
        // Using TypeScript DataTableQueryBackendService
        return createTypeScriptDataTableQueryBackendService($injector, AppConfig);
      }
      // Using Angular DataTableQueryBackendService
      return $delegate;
    } catch (error) {
      // Error in DataTableQueryBackendService decorator - fallback to original
      return $delegate;
    }
  });
});

function createTypeScriptDataTableBackendService(injector, AppConfig) {
  // Check for Angular services we still need
  const requiredAngularServices = ["DataFilter", "queuedHttp", "RecordsetAdapter", "TableAdapter"];

  const services = {};
  const missingServices = [];

  for (const serviceName of requiredAngularServices) {
    try {
      services[serviceName] = injector.get(serviceName);
    } catch (e) {
      missingServices.push(serviceName);
    }
  }

  if (missingServices.length > 0) {
    throw new Error("Missing services: " + missingServices.join(", "));
  }

  // Create TypeScript service instance using ES6 modules + Angular services
  const typeScriptServiceInstance = new DataTableBackendService(
    AppConfig,
    ApiPath, // ES6 module
    services.DataFilter, // Angular service
    services.queuedHttp, // Angular service
    services.RecordsetAdapter, // Angular service
    services.TableAdapter, // Angular service
    backendApi // ES6 module
  );

  // Create a properly bound service object with all methods
  const typeScriptService = {
    getTableMetadata: typeScriptServiceInstance.getTableMetadata.bind(typeScriptServiceInstance),
    getTableData: typeScriptServiceInstance.getTableData.bind(typeScriptServiceInstance),
    getFilteredRecordCount: typeScriptServiceInstance.getFilteredRecordCount.bind(typeScriptServiceInstance),
    getCommentCounts: typeScriptServiceInstance.getCommentCounts.bind(typeScriptServiceInstance),
    fieldValuesRequested: typeScriptServiceInstance.fieldValuesRequested.bind(typeScriptServiceInstance),
    tableRequested: typeScriptServiceInstance.tableRequested.bind(typeScriptServiceInstance),
    tableRequestedForMetric: typeScriptServiceInstance.tableRequestedForMetric.bind(typeScriptServiceInstance),
    recordsetRequested: typeScriptServiceInstance.recordsetRequested.bind(typeScriptServiceInstance),
    deleteField: typeScriptServiceInstance.deleteField.bind(typeScriptServiceInstance),
    _implementationType: "TypeScript",
    _migrationVersion: "v2.0",
  };

  // TypeScript DataTableBackendService created successfully
  return typeScriptService;
}

function createTypeScriptDataTableQueryBackendService(injector, AppConfig) {
  // Check for Angular services we still need
  const requiredAngularServices = ["DataFilter", "RecordsetAdapter", "TableAdapter"];

  const services = {};
  const missingServices = [];

  for (const serviceName of requiredAngularServices) {
    try {
      services[serviceName] = injector.get(serviceName);
    } catch (e) {
      missingServices.push(serviceName);
    }
  }

  if (missingServices.length > 0) {
    throw new Error("Missing services: " + missingServices.join(", "));
  }

  // Create TypeScript service instance using ES6 modules + Angular services
  const typeScriptServiceInstance = new DataTableQueryBackendService(
    AppConfig,
    services.DataFilter, // Angular service
    services.RecordsetAdapter, // Angular service
    services.TableAdapter, // Angular service
    QueryClient, // ES6 module
    QueryGenerator, // ES6 module
    QueryErrorInterpreter, // ES6 module
    TableDataAdapter // ES6 module
  );

  // Create a properly bound service object
  const typeScriptService = {
    getTableMetadata: typeScriptServiceInstance.getTableMetadata.bind(typeScriptServiceInstance),
    recordsetRequested: typeScriptServiceInstance.recordsetRequested.bind(typeScriptServiceInstance),
    _implementationType: "TypeScript",
    _migrationVersion: "v2.0",
  };

  return typeScriptService;
}

// Debug functions
if (typeof window !== "undefined") {
  window.checkDataTableMigration = function() {
    try {
      const injector = angular.element(document.body).injector();
      const backendService = injector.get("DataTableBackendService");
      const queryService = injector.get("DataTableQueryBackendService");

      // Return migration status for debugging
      return {
        backendServiceType: backendService._implementationType || "Unknown",
        queryServiceType: queryService._implementationType || "Unknown",
      };
    } catch (error) {
      console.error("Error checking migration status:", error);
      return null;
    }
  };
}
